import { api, ApiCallOptions } from '~/api';
import {
    Application,
    CandidateStatus,
    CandidateVerificationStatus,
    DisqualificationReason,
    JobOpeningWithCompanyName,
    LinkedinResumeStatus,
    Message,
    MessageWithPoster,
} from '~/types/domain.ts';
import {
    Activity,
    CandidateAddNoteRequest,
    CandidateApplyToJobPositionRequest,
    CandidateGetProfileResponse,
    CandidatePublicProfileResponse,
    EditCandidateLocationRequest,
    EditLeadRequest,
    IMThreadsResponse,
    RegisterCustomerResponse,
    SetExpectedSalaryRequest,
    TagResponse,
    TalentSpecialistForm,
} from '~/types/platform.ts';

const call = <T>(path: string, opts: ApiCallOptions = {}) => api<T>(`/candidates` + path, opts);
const tagCall = <T>(path: string, opts: ApiCallOptions = {}) => api<T>(`/tags` + path, opts);

export async function getAvailableJobOpenings(id: number) {
    return call<JobOpeningWithCompanyName[]>(`/${id}/available-job-openings`);
}

export async function applyToJobOpening(id: number, body: CandidateApplyToJobPositionRequest) {
    return call<Application>(`/${id}/apply`, { method: 'POST', body });
}

export async function getProfile(id: number) {
    return call<CandidateGetProfileResponse>(`/${id}/profile`);
}

export async function getActivity(id: number) {
    return call<Activity[]>(`/${id}/activity`);
}

export async function reprocessLinkedInProfile(id: number) {
    return call<LinkedinResumeStatus>(`/${id}/reprocess_linkedin`, { method: 'POST' });
}

export async function getPublicProfile(id: string, extra: Record<string, string> = {}) {
    const query = Object.keys(extra)
        .map((k) => `${k}=${extra[k]}`)
        .join('&');
    return api<CandidatePublicProfileResponse>(
        `/public/candidates/${id}/profile${query ? `?${query}` : ''}`,
    );
}

export async function editCandidateTalentSpecialistInterview(
    id: number,
    body: TalentSpecialistForm,
) {
    return call<number>(`/${id}/interview`, { method: 'PATCH', body });
}

export async function setAvailability(id: number, open: boolean, period = -1, reason = '') {
    return call<boolean>(`/${id}/availability`, {
        method: 'PATCH',
        body: {
            open,
            period,
            reason,
        },
    });
}

export async function editCandidateExpectedSalary(id: number, body: SetExpectedSalaryRequest) {
    return call<boolean>(`/${id}/expected_salary`, { method: 'PATCH', body });
}

export async function addReferral(id: number, referral: number) {
    return call<number>(`/${id}/referral`, { method: 'PATCH', body: { referral } });
}

export async function moveLead(id: number, status: CandidateStatus) {
    return call<boolean>(`/lead/${id}/move`, { method: 'POST', body: { status } });
}

export async function disqualifyLead(id: number, reason: DisqualificationReason) {
    return call<boolean>(`/lead/${id}/disqualify`, { method: 'POST', body: { reason } });
}

export async function qualifyLead(id: number) {
    return call<boolean>(`/lead/${id}/qualify`, { method: 'POST' });
}

export async function updateLead(id: number, body: EditLeadRequest) {
    return call<boolean>(`/lead/${id}`, { method: 'PATCH', body });
}

export async function getIntroAudio(id: number): Promise<Blob> {
    return api.raw(`/candidates/${id}/intro_audio`).then((r) => r.blob());
}

export async function getIntroTranscript(id: number): Promise<Blob> {
    return api.raw(`/candidates/${id}/intro_transcript`).then((r) => r.blob());
}

export async function getProfileBuilderAudio(id: number): Promise<Blob> {
    return api.raw(`/candidates/${id}/profile_builder_audio`).then((r) => r.blob());
}

export async function getProfileBuilderTranscript(id: number): Promise<Blob> {
    return api.raw(`/candidates/${id}/profile_builder_transcript`).then((r) => r.blob());
}

export async function getSkillTags() {
    return tagCall<TagResponse[]>(`/list/skill`, { method: 'GET' });
}

export async function getExpertiseTags() {
    return tagCall<TagResponse[]>(`/list/expertise`, { method: 'GET' });
}

export async function getSectorTags() {
    return tagCall<TagResponse[]>(`/list/sector`, { method: 'GET' });
}

export async function getOtherTags() {
    return tagCall<TagResponse[]>(`/list/other`, { method: 'GET' });
}

export async function listNotes(id: number) {
    return call<MessageWithPoster[]>(`/${id}/notes`, { method: 'GET' });
}

export async function createNote(id: number, body: CandidateAddNoteRequest) {
    return call<Message>(`/${id}/notes`, { method: 'POST', body });
}

export async function editNote(id: number, note_id: number, body: CandidateAddNoteRequest) {
    return call<Message>(`/${id}/notes/${note_id}`, { method: 'PATCH', body });
}

export async function deleteNote(id: number, note_id: number) {
    return call<boolean>(`/${id}/notes/${note_id}`, { method: 'DELETE' });
}

export async function registerCustomer(name: string, email: string) {
    return api.auth<RegisterCustomerResponse>('/register/customer', {
        method: 'POST',
        body: { name, email },
    });
}

export async function requestVerification(id: number) {
    return call<CandidateVerificationStatus>(`/${id}/verification`, { method: 'POST' });
}

export async function requestDeletion(id: number) {
    return call<string>(`/${id}/request_deletion`, { method: 'POST' });
}

export async function reset2FA(id: number) {
    return call<boolean>(`/${id}/2fa`, { method: 'DELETE' });
}

export async function toggleProfileBuilderExperiment(id: number) {
    return call<boolean>(`/${id}/profile_builder_experiment`, { method: 'POST' });
}

export async function toggleProfileComplete(id: number) {
    return call<boolean>(`/${id}/profile_complete`, { method: 'POST' });
}

export async function editLocation(id: number, body: EditCandidateLocationRequest) {
    return call<boolean>(`/${id}/location`, { method: 'PATCH', body });
}

export async function getImThreads(candidateId: number) {
    return call<IMThreadsResponse>(`/${candidateId}/im/threads`);
}

export async function createImThread(candidateId: number) {
    return call<number>(`/${candidateId}/im/threads`, { method: 'POST' });
}
